import { createSelector } from 'reselect'
import { getActiveSplitCardLegs } from 'checkout/lib/splitPaymentAllocation'
import { getCheckoutLoyaltyBurnTotalInPoints } from 'checkout/selectors/loyalty/getCheckoutLoyaltyBurnTotals'
import getCheckoutLoyaltyEarnDiscountPaymentTypes from 'checkout/selectors/loyalty/pointsCalculation/getCheckoutLoyaltyEarnDiscountPaymentTypes'
import getCheckoutTotalsView from 'checkout/selectors/payment/getCheckoutTotalsView'
import { getCheckoutCurrency } from 'checkout/selectors/payment/paymentType'
import { getVisibleCheckoutSavedPaymentCards } from 'checkout/selectors/payment/savedCards'
import { EmptyArray } from 'lib/array/arrayUtils'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getLoyaltyEarnPaymentMethods from 'loyalty/lib/paymentMethodEarn/getLoyaltyEarnPaymentMethods'
import getIsLoyaltyPaymentMethodEarnEnabled from 'loyalty/selectors/getIsLoyaltyPaymentMethodEarnEnabled'

// The cards paying an active split payment, leaving out any card with nothing allocated to it
const getCheckoutSplitPaymentLegs = createSelector(
  (state: App.State) => state.checkout.splitPayment.isActive,
  (state: App.State) => state.checkout.splitPayment.legs,
  (state: App.State) => getCheckoutCurrency(state),
  (isActive, legs, currencyCode) => (isActive ? getActiveSplitCardLegs(legs ?? EmptyArray, currencyCode) : undefined),
)

/**
 * The payments sent with checkout earn requests for payment-method earn. Post-purchase checkouts send
 * none because they keep the original order's earn rate, whatever pays for the change.
 */
const getCheckoutLoyaltyEarnPaymentMethods = createSelector(
  (state: App.State) => getIsLoyaltyPaymentMethodEarnEnabled(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.displayedPaymentMethod,
  (state: App.State) => state.checkout.stripePaymentMethod,
  (state: App.State) => getCheckoutSplitPaymentLegs(state),
  (state: App.State) => state.checkout.splitPayment.newCardDetailsByLegId,
  (state: App.State) => getVisibleCheckoutSavedPaymentCards(state),
  (state: App.State) => getCheckoutTotalsView(state),
  (state: App.State) => getCheckoutLoyaltyBurnTotalInPoints(state),
  (state: App.State) => getCheckoutLoyaltyEarnDiscountPaymentTypes(state),
  (
    isPaymentMethodEarnEnabled,
    postPurchaseMode,
    displayedPaymentMethod,
    stripePaymentMethod,
    splitPaymentLegs,
    splitNewCardDetailsByLegId,
    savedCards,
    totalsView,
    pointsBurnAmount,
    discountPaymentTypes,
  ): Array<App.LoyaltyEarnPaymentMethod> | undefined => {
    if (!isPaymentMethodEarnEnabled || postPurchaseMode) return undefined

    return getLoyaltyEarnPaymentMethods({
      displayedPaymentMethod,
      stripePaymentMethod,
      splitPaymentLegs,
      splitNewCardDetailsByLegId,
      savedCards,
      creditPaymentAmount: totalsView.data.creditPaymentAmount,
      giftCardPaymentAmount: totalsView.data.giftCardPaymentAmount,
      pointsBurnAmount,
      velocityPointsApplied: totalsView.data.velocityPointsApplied ?? 0,
      discountPaymentTypes,
    })
  },
)

export default getCheckoutLoyaltyEarnPaymentMethods
